/**
 * Leaderboard component — displays team rankings by game type.
 */

import { Trophy, Medal, Shield } from "lucide-react";
import { useLeaderboard, type LeaderboardPeriod } from "@/hooks/useLeaderboard";
import { getRatingTier } from "@/lib/ratings";
import { GAME_TICTACTOE } from "@/lib/gameKinds";
import { useState } from "react";

const PERIODS: { value: LeaderboardPeriod; label: string }[] = [
  { value: "all", label: "All Time" },
  { value: "monthly", label: "Monthly" },
  { value: "weekly", label: "Weekly" },
];

export function Leaderboard() {
  const [period, setPeriod] = useState<LeaderboardPeriod>("all");
  const { entries, isLoading } = useLeaderboard(GAME_TICTACTOE);

  const getRankIcon = (rank: number) => {
    switch (rank) {
      case 1:
        return <Trophy className="h-5 w-5 text-yellow-400" />;
      case 2:
        return <Medal className="h-5 w-5 text-slate-300" />;
      case 3:
        return <Medal className="h-5 w-5 text-amber-600" />;
      default:
        return <span className="text-sm font-mono text-muted-foreground w-5 text-center">{rank}</span>;
    }
  };

  return (
    <div>
      {/* Period filter */}
      <div className="mb-6 flex gap-1 rounded-lg bg-muted p-1 w-fit">
        {PERIODS.map((p) => (
          <button
            key={p.value}
            onClick={() => setPeriod(p.value)}
            className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
              period === p.value
                ? "bg-background text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Leaderboard list */}
      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="flex items-center gap-4 rounded-xl border border-border bg-card p-4 animate-pulse">
              <div className="h-8 w-8 rounded-full bg-muted" />
              <div className="flex-1 space-y-2">
                <div className="h-4 w-24 rounded bg-muted" />
                <div className="h-3 w-32 rounded bg-muted" />
              </div>
              <div className="h-6 w-12 rounded bg-muted" />
            </div>
          ))}
        </div>
      ) : entries.length === 0 ? (
        <div className="rounded-xl border border-border bg-card p-12 text-center">
          <Trophy className="mx-auto mb-3 h-10 w-10 text-muted-foreground/40" />
          <p className="text-muted-foreground">No teams on the leaderboard yet.</p>
          <p className="text-sm text-muted-foreground/60">Be the first to compete and claim your spot!</p>
        </div>
      ) : (
        <div className="space-y-2">
          {entries.map((entry) => {
            const tier = getRatingTier(entry.rating.rating);
            const winRate = entry.rating.gamesPlayed > 0
              ? Math.round((entry.rating.wins / entry.rating.gamesPlayed) * 100)
              : 0;

            return (
              <div
                key={entry.teamId}
                className={`flex items-center gap-4 rounded-xl border p-4 transition-colors ${
                  entry.rank <= 3
                    ? "border-primary/20 bg-primary/5"
                    : "border-border bg-card hover:bg-accent"
                }`}
              >
                {/* Rank */}
                <div className="flex h-8 w-8 items-center justify-center">
                  {getRankIcon(entry.rank)}
                </div>

                {/* Team info */}
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Shield className="h-4 w-4 text-muted-foreground" />
                    <p className="font-semibold text-foreground">{entry.teamName}</p>
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${tier.color}`}>
                      {tier.name}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {entry.rating.wins}W / {entry.rating.losses}L / {entry.rating.draws}D
                    <span className="mx-1.5">·</span>
                    {winRate}% win rate
                    <span className="mx-1.5">·</span>
                    {entry.rating.gamesPlayed} games
                  </p>
                </div>

                {/* Rating */}
                <div className="text-right">
                  <p className="text-lg font-bold text-foreground tabular-nums">
                    {entry.rating.rating}
                  </p>
                  <p className="text-[10px] text-muted-foreground">ELO</p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}